import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { InterventionAreas } from '../../types/policyImpact';
import { useTheme } from '../../contexts/ThemeContext';
import {
  SCENARIOS_EXPAND_ADMIN_MESSAGE,
  SCENARIOS_EXPAND_ADMIN_SUBTEXT,
} from '../../constants/membershipAccess';
import { MEMBERSHIP_APPLY_PATH, SCHEDULE_DEMO_PATH } from '../../constants/dataUsePolicy';
import { useClimatePlansAdmin } from '../../hooks/useClimatePlansAccess';
import { GROWTH_RATE_PERCENT_MAX } from '../../services/compactDevelopmentService';
import './InterventionAreasControl.css';

interface SubPolicyValues {
  transitOrientedDevelopment: number;
  downtownHomesPercent: number; // % of homes in or near downtown (≤½ distance); default 70; 29% fewer VMT
  activeTransportation: number;
  transitImprovements: number;
  parkingPricing: number;
  micromobility: number;
  otherInitiatives: number; // User-adjustable
}

/** Energy Efficiency sub-policy sliders (mirrors EnergyEfficiencyComponents; Other is read-only residual). */
export interface EnergyEfficiencySubPolicyValues {
  residentialReachCodePercent: number;
  residentialRetrofitExistingPercent: number;
  commercialRetrofitExistingPercent: number;
  otherEfficiencyPercent: number;
}

interface InterventionAreasControlProps {
  interventionAreas: InterventionAreas;
  onChange: (updatedAreas: InterventionAreas) => void;
  /** Controlled expansion state (from parent); when provided, parent can auto-expand e.g. when efficiency slider moved. */
  expandedIntervention?: keyof InterventionAreas | null;
  onExpandedChange?: (expanded: keyof InterventionAreas | null) => void;
  subPolicyValues?: SubPolicyValues;
  onSubPolicyChange?: (subPolicyValues: SubPolicyValues) => void;
  onSubPolicyExpand?: (key: string | null) => void;
  energyEfficiencySubPolicyValues?: EnergyEfficiencySubPolicyValues;
  onEnergyEfficiencySubPolicyChange?: (values: EnergyEfficiencySubPolicyValues) => void;
  /** Expanded EE only: combined-% residual (headline − model total from named levers), read-only row. */
  energyEfficiencyOtherResidualDisplay?: number;
  parkingVMTReductionPercent?: number; // Calculated parking pricing VMT reduction %
  computeParkingVMTReductionPercent?: (priceIncreasePercent: number) => number; // Function to calculate parking VMT reduction
  micromobilityVMTReductionPercent?: number; // Calculated micromobility VMT reduction %
  transitVMTReductionPercent?: number; // Calculated transit service VMT reduction %
  /** BAU housing growth %/yr — left end of Compact Development slider (like EV min = current share). */
  compactDevelopmentMinPercent?: number;
  /** Incremental VMT reduction % from compact model, included in headline VMT slider. */
  compactDevelopmentVmtReductionPercent?: number;
  onLoadScenario?: (scenarioName: string) => void; // Handler for loading predefined scenarios
  onResetScenario?: () => void; // Handler for resetting scenario
  activeScenarioName?: string | null; // Currently active predefined scenario name
  onClearScenarioName?: () => void; // Clear the active scenario name (e.g. when a slider is manually changed)
}

interface InterventionData {
  key: keyof InterventionAreas;
  label: string;
  description: string;
  min: number;
  max: number;
  color: string;
  bgColor: string;
}

const interventions: InterventionData[] = [
  // Transportation - Light Blue
  { key: 'vmtReduction', label: 'VMT Reduction', description: 'Reduce vehicle miles traveled through transit, biking, and other initiatives', min: 0, max: 20, color: '#bfdbfe', bgColor: '#eff6ff' },
  {
    key: 'vehicleElectrification',
    label: 'Vehicle Electrification beyond BAU',
    description:
      'Additional light-duty ZEV ambition beyond the EMFAC business-as-usual pathway. 0% = BAU fleet EV share in 2045 (no extra local ZEV). 100% = 100% ZEV by 2045. Moving the slider only adds reductions above BAU.',
    min: 0,
    max: 100,
    color: '#bfdbfe',
    bgColor: '#eff6ff',
  },
  // Buildings - Yellow
  { key: 'energyEfficiency', label: 'Energy Efficiency', description: 'Improve building energy efficiency through retrofits and standards', min: 0, max: 40, color: '#fef08a', bgColor: '#fefce8' },
  { key: 'buildingsElectrification', label: 'Additional Building Electrification Beyond BAU', description: 'Additional share of remaining fossil building systems converted to electric by 2045', min: 0, max: 100, color: '#fef08a', bgColor: '#fefce8' },
  { key: 'gridDecarbonization', label: 'Clean Electricity Year', description: 'Year electricity reaches zero emissions (earlier = faster grid decarbonization)', min: 2025, max: 2045, color: '#fef08a', bgColor: '#fefce8' },
  // Other - Orange
  {
    key: 'organicsDiversion',
    label: 'Organics Diversion (Under construction)',
    description: 'Additional organics diversion beyond BAU (state policy already included). Expanded details panel is under construction; the slider still updates the forecast.',
    min: 0,
    max: 50,
    color: '#fed7aa',
    bgColor: '#fff7ed',
  },
];

const SLIDER_TRACK_EMPTY_LIGHT = '#e5e7eb';
const SLIDER_TRACK_EMPTY_DARK = '#475569';

const InterventionAreasControl: React.FC<InterventionAreasControlProps> = ({
  interventionAreas,
  onChange,
  expandedIntervention: expandedInterventionProp,
  onExpandedChange,
  subPolicyValues = { transitOrientedDevelopment: 0, downtownHomesPercent: 70, activeTransportation: 0, transitImprovements: 0, parkingPricing: 0, micromobility: 0, otherInitiatives: 0 },
  onSubPolicyChange,
  onSubPolicyExpand,
  energyEfficiencySubPolicyValues = {
    residentialReachCodePercent: 0,
    residentialRetrofitExistingPercent: 0,
    commercialRetrofitExistingPercent: 0,
    otherEfficiencyPercent: 0,
  },
  onEnergyEfficiencySubPolicyChange,
  energyEfficiencyOtherResidualDisplay,
  parkingVMTReductionPercent = 0,
  computeParkingVMTReductionPercent,
  micromobilityVMTReductionPercent = 0,
  transitVMTReductionPercent = 0,
  compactDevelopmentMinPercent = 0,
  compactDevelopmentVmtReductionPercent = 0,
  onLoadScenario,
  onResetScenario,
  activeScenarioName,
  onClearScenarioName,
}) => {
  const { theme } = useTheme();
  const { isAdmin: canExpandDetails } = useClimatePlansAdmin();
  const sliderTrackEmpty = theme === 'dark' ? SLIDER_TRACK_EMPTY_DARK : SLIDER_TRACK_EMPTY_LIGHT;
  // Clean Electricity Year slider UX:
  // - Left = 2045 (higher emissions), Right = 2025 (lower emissions)
  // Internally, we still store the actual year (2025-2045) in interventionAreas.gridDecarbonization.
  const CLEAN_ELEC_YEAR_MIN = 2025;
  const CLEAN_ELEC_YEAR_MAX = 2045;
  const cleanYearToUi = (year: number) => {
    const y = Math.max(CLEAN_ELEC_YEAR_MIN, Math.min(CLEAN_ELEC_YEAR_MAX, Number(year) || CLEAN_ELEC_YEAR_MAX));
    return CLEAN_ELEC_YEAR_MAX - y; // 0..20
  };
  const uiToCleanYear = (uiValue: number) => {
    const v = Math.max(0, Math.min(CLEAN_ELEC_YEAR_MAX - CLEAN_ELEC_YEAR_MIN, Number(uiValue) || 0));
    return CLEAN_ELEC_YEAR_MAX - v;
  };
  const scenarioLabels: Record<string, string> = {
    baseline: 'Baseline',
    fullImplementation: 'Full Implementation',
    aggressive: 'Aggressive',
    moderate: 'Moderate',
    electrificationFocus: 'Electrification Focus',
  };

  const [expandedInterventionLocal, setExpandedInterventionLocal] = useState<keyof InterventionAreas | null>(null);
  const expandedIntervention = expandedInterventionProp !== undefined ? expandedInterventionProp : expandedInterventionLocal;
  const setExpandedIntervention = (v: keyof InterventionAreas | null) => {
    if (onExpandedChange) onExpandedChange(v);
    if (expandedInterventionProp === undefined) setExpandedInterventionLocal(v);
  };
  const [expandedSubPolicy, setExpandedSubPolicy] = useState<string | null>(null);
  const [scenarioDropdownOpen, setScenarioDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  /** Local draft for Energy Efficiency sub-sliders so dragging does not re-run PolicyImpactTool heavy work every tick. */
  const [localEnergyEfficiency, setLocalEnergyEfficiency] = useState<EnergyEfficiencySubPolicyValues>(energyEfficiencySubPolicyValues);
  const energyEfficiencyDragActiveRef = useRef(false);

  useEffect(() => {
    if (!energyEfficiencyDragActiveRef.current) {
      setLocalEnergyEfficiency(energyEfficiencySubPolicyValues);
    }
  }, [energyEfficiencySubPolicyValues]);

  useEffect(() => {
    if (canExpandDetails) return;
    setExpandedIntervention(null);
    setExpandedSubPolicy(null);
    onSubPolicyExpand?.(null);
  }, [canExpandDetails, onSubPolicyExpand]);

  const finishEnergyEfficiencyDrag = React.useCallback(() => {
    if (!energyEfficiencyDragActiveRef.current) return;
    energyEfficiencyDragActiveRef.current = false;
    setLocalEnergyEfficiency((current) => {
      onEnergyEfficiencySubPolicyChange?.(current);
      return current;
    });
  }, [onEnergyEfficiencySubPolicyChange]);

  const handleEnergyEfficiencySliderInput = (key: keyof EnergyEfficiencySubPolicyValues, value: number) => {
    if (!onEnergyEfficiencySubPolicyChange || key === 'otherEfficiencyPercent') return;
    energyEfficiencyDragActiveRef.current = true;
    setLocalEnergyEfficiency((prev) => ({ ...prev, [key]: value }));
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setScenarioDropdownOpen(false);
      }
    };

    if (scenarioDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [scenarioDropdownOpen]);
  
  // Sum of policy reductions that drive the top-level VMT slider (includes incremental compact VMT % from model).
  // transitImprovements: use transitVMTReductionPercent (calculated), not service increase %.
  const sumOfPolicyReductions =
    (subPolicyValues.activeTransportation || 0) +
    transitVMTReductionPercent +
    parkingVMTReductionPercent +
    micromobilityVMTReductionPercent +
    (compactDevelopmentVmtReductionPercent || 0);

  
  // Other Initiatives: when VMT Reduction is expanded, calculate from top slider
  // When collapsed, use the stored value
  const otherInitiativesValue = expandedIntervention === 'vmtReduction'
    ? Math.max(0, interventionAreas.vmtReduction - sumOfPolicyReductions)
    : (subPolicyValues.otherInitiatives || 0);
  
  // VMT Reduction sub-policies
  // Note: For parking pricing and micromobility, the slider controls the policy level (price increase % or scale %),
  // not the resulting VMT reduction percentage. The VMT reduction is calculated separately.
  const compactGrowthMin =
    typeof compactDevelopmentMinPercent === 'number' && Number.isFinite(compactDevelopmentMinPercent)
      ? Math.min(GROWTH_RATE_PERCENT_MAX, Math.max(0, compactDevelopmentMinPercent))
      : 0;
  const compactGrowthValue = Math.min(
    GROWTH_RATE_PERCENT_MAX,
    Math.max(compactGrowthMin, subPolicyValues.transitOrientedDevelopment ?? 0),
  );
  const vmtSubPolicies = [
    {
      key: 'transitOrientedDevelopment',
      label: 'Compact Development (% housing growth/yr, min = BAU)',
      min: compactGrowthMin,
      max: GROWTH_RATE_PERCENT_MAX,
      value: compactGrowthValue,
    },
    {
      key: 'activeTransportation',
      label: 'Expand active transportation infrastructure by (Under construction)',
      min: 0,
      max: 10,
      value: subPolicyValues.activeTransportation || 0,
    },
    { key: 'transitImprovements', label: 'Expand transit service by', min: 0, max: 50, value: subPolicyValues.transitImprovements || 0 },
    { key: 'parkingPricing', label: 'Increase parking price by', min: 0, max: 300, value: subPolicyValues.parkingPricing || 0 },
    { key: 'micromobility', label: 'Expand micromobility by', min: 0, max: 200, value: subPolicyValues.micromobility || 0 },
    { key: 'otherInitiatives', label: '% Reduction Other Initiatives', min: 0, max: 50, value: otherInitiativesValue },
  ];

  const energyEfficiencyOtherShown =
    expandedIntervention === 'energyEfficiency' && typeof energyEfficiencyOtherResidualDisplay === 'number'
      ? energyEfficiencyOtherResidualDisplay
      : localEnergyEfficiency.otherEfficiencyPercent;

  const energyEfficiencySubPolicies = [
    {
      key: 'residentialReachCodePercent' as const,
      label: 'Improve efficiency of new residential buildings by',
      min: 0,
      max: 40,
      value: localEnergyEfficiency.residentialReachCodePercent,
    },
    {
      key: 'residentialRetrofitExistingPercent' as const,
      label: 'Improve efficiency of existing residential buildings by',
      min: 0,
      max: 50,
      value: localEnergyEfficiency.residentialRetrofitExistingPercent,
    },
    {
      key: 'commercialRetrofitExistingPercent' as const,
      label: 'Improve efficiency of existing commercial buildings by',
      min: 0,
      max: 50,
      value: localEnergyEfficiency.commercialRetrofitExistingPercent,
    },
    {
      key: 'otherEfficiencyPercent' as const,
      label: '% Reduction Other Initiatives',
      min: 0,
      max: 40,
      value: energyEfficiencyOtherShown,
    },
  ];

  const handleSubPolicyValueChange = (key: string, value: number) => {
    if (onClearScenarioName) onClearScenarioName();
    if (onSubPolicyChange) {
      let v = value;
      if (key === 'transitOrientedDevelopment') {
        v = Math.min(GROWTH_RATE_PERCENT_MAX, Math.max(compactGrowthMin, value));
      }
      const updatedValues = {
        ...subPolicyValues,
        [key as keyof SubPolicyValues]: v,
      };
      onSubPolicyChange(updatedValues);

      // Other Initiatives is read-only (indicator only), so skip handling changes to it
      if (key === 'otherInitiatives') {
        return;
      }
      // Compact Development: parent recalculates net statewide → compact VMT % → headline VMT (debounced).
      if (key === 'transitOrientedDevelopment') {
        return;
      }
      // Parking pricing and micromobility: calculated percentages trigger PolicyImpactTool effect to update total
      if (key === 'parkingPricing' || key === 'micromobility') {
        return;
      }
      // When other sub-policies change, update headline VMT to match sum (includes compact VMT % from model).
      const updatedSumOfPolicyReductions =
        (key === 'activeTransportation' ? value : (subPolicyValues.activeTransportation || 0)) +
        transitVMTReductionPercent +
        parkingVMTReductionPercent +
        micromobilityVMTReductionPercent +
        (compactDevelopmentVmtReductionPercent || 0);
      const newTotalVmtReduction = updatedSumOfPolicyReductions + otherInitiativesValue;
      onChange({
        ...interventionAreas,
        vmtReduction: Math.max(0, Math.min(20, newTotalVmtReduction)),
      });
    }
  };
  
  const handleExpandClick = (key: keyof InterventionAreas) => {
    if (!canExpandDetails) return;
    const togglingClosed = expandedIntervention === key;
    if (
      expandedIntervention === 'energyEfficiency' &&
      (togglingClosed || key !== 'energyEfficiency')
    ) {
      finishEnergyEfficiencyDrag();
    }
    const newExpanded = togglingClosed ? null : key;
    setExpandedIntervention(newExpanded);
    // Scroll to details section for vehicle electrification
    if (newExpanded === 'vehicleElectrification') {
      setTimeout(() => {
        const element = document.getElementById('vehicle-electrification-details');
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    // Scroll to details section for building electrification
    if (newExpanded === 'buildingsElectrification') {
      setTimeout(() => {
        const element = document.getElementById('building-electrification-details');
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
    // Scroll to details section for energy efficiency
    if (newExpanded === 'energyEfficiency') {
      setTimeout(() => {
        const element = document.getElementById('energy-efficiency-details');
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleSubPolicyClick = (key: string) => {
    if (!canExpandDetails) return;
    const newKey = expandedSubPolicy === key ? null : key;
    setExpandedSubPolicy(newKey);
    if (onSubPolicyExpand) onSubPolicyExpand(newKey);
    if (onExpandedChange) onExpandedChange(newKey ? 'vmtReduction' : null);
    if (
      newKey &&
      (newKey === 'parkingPricing' ||
        newKey === 'micromobility' ||
        newKey === 'transitImprovements' ||
        newKey === 'transitOrientedDevelopment' ||
        newKey === 'activeTransportation')
    ) {
      setTimeout(() => {
        const element = document.getElementById(
          newKey === 'parkingPricing'
            ? 'parking-pricing-details'
            : newKey === 'micromobility'
            ? 'micromobility-details'
            : newKey === 'transitImprovements'
            ? 'transit-service-details'
            : newKey === 'activeTransportation'
            ? 'active-transportation-details'
            : 'compact-development-details'
        );
        if (element) element.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  const handleChange = (key: keyof InterventionAreas, value: number) => {
    if (onClearScenarioName) onClearScenarioName();

    const clamped =
      key === 'vehicleElectrification'
        ? Math.max(0, Math.min(100, value))
        : value;
    onChange({
      ...interventionAreas,
      [key]: clamped,
    });
    
    // If VMT Reduction changed, update Other Initiatives to balance
    if (key === 'vmtReduction' && onSubPolicyChange) {
      const calculatedOtherInitiatives = Math.max(0, clamped - sumOfPolicyReductions);
      
      onSubPolicyChange({
        ...subPolicyValues,
        otherInitiatives: calculatedOtherInitiatives,
      });
    }
  };

  const handleScenarioSelect = (scenarioName: string) => {
    if (onLoadScenario) {
      onLoadScenario(scenarioName);
    }
    setScenarioDropdownOpen(false);
  };

  const handleResetClick = () => {
    if (onResetScenario) {
      onResetScenario();
    }
    setScenarioDropdownOpen(false);
  };

  return (
    <div className="intervention-areas-control">
      <div className="intervention-areas-header">
        <div>
          <h3 className="intervention-areas-title">Implementation by 2045</h3>
          <p className="intervention-areas-description">
            Adjust each intervention to set ambition by 2045. Vehicle electrification is beyond BAU
            (0% = EMFAC pathway, 100% = full ZEV)—reductions appear only above BAU.
          </p>
        </div>
        {(onLoadScenario || onResetScenario) && (
          <div className="scenario-dropdown-container" ref={dropdownRef}>
            <button
              className="scenario-dropdown-button"
              onClick={() => setScenarioDropdownOpen(!scenarioDropdownOpen)}
              aria-expanded={scenarioDropdownOpen}
            >
              {activeScenarioName ? scenarioLabels[activeScenarioName] || activeScenarioName : 'Quick Scenarios'}
              <span className="dropdown-arrow">{scenarioDropdownOpen ? '▲' : '▼'}</span>
            </button>
            {scenarioDropdownOpen && (
              <div className="scenario-dropdown-menu">
                {onLoadScenario && (
                  <>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('baseline')}
                    >
                      Baseline
                    </button>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('fullImplementation')}
                    >
                      Full Implementation
                    </button>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('aggressive')}
                    >
                      Aggressive
                    </button>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('moderate')}
                    >
                      Moderate
                    </button>
                    <button
                      className="scenario-dropdown-item"
                      onClick={() => handleScenarioSelect('electrificationFocus')}
                    >
                      Electrification Focus
                    </button>
                  </>
                )}
                {onResetScenario && (
                  <button
                    className="scenario-dropdown-item scenario-dropdown-item-reset"
                    onClick={handleResetClick}
                  >
                    Reset
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {!canExpandDetails ? (
        <div className="intervention-expand-member-notice" role="note">
          <p className="intervention-expand-member-notice__message">{SCENARIOS_EXPAND_ADMIN_MESSAGE}</p>
          <p className="intervention-expand-member-notice__subtext">{SCENARIOS_EXPAND_ADMIN_SUBTEXT}</p>
          <div className="intervention-expand-member-notice__actions">
            <Link to={MEMBERSHIP_APPLY_PATH} className="intervention-expand-member-notice__btn">
              Become a Member
            </Link>
            <Link to={SCHEDULE_DEMO_PATH} className="intervention-expand-member-notice__btn">
              Schedule a Demo
            </Link>
          </div>
        </div>
      ) : null}

      <div className="intervention-table">
        {interventions.map((intervention) => (
          <React.Fragment key={intervention.key}>
            <div 
              className="intervention-table-row"
              style={{ backgroundColor: intervention.bgColor, borderLeftColor: intervention.color }}
            >
            <div className="intervention-table-label-cell">
              {canExpandDetails ? (
                <button
                  className="intervention-info-button-small"
                  onClick={() => handleExpandClick(intervention.key)}
                  title={intervention.description}
                  aria-expanded={expandedIntervention === intervention.key}
                >
                  {expandedIntervention === intervention.key ? '−' : '+'}
                </button>
              ) : (
                <span
                  className="intervention-info-button-small intervention-info-button-small--locked"
                  title="Detailed controls available to members"
                  aria-hidden="true"
                >
                  +
                </span>
              )}
              <span className="intervention-item-label-compact">{intervention.label}</span>
            </div>
            <div className="intervention-table-slider-cell">
              <div className="compact-slider-wrapper">
                <input
                  type="range"
                  disabled={
                    (intervention.key === 'vmtReduction' && expandedIntervention === 'vmtReduction') ||
                    (intervention.key === 'energyEfficiency' && expandedIntervention === 'energyEfficiency')
                  }
                  min={intervention.key === 'gridDecarbonization'
                    ? 0
                    : intervention.min}
                  max={intervention.key === 'gridDecarbonization'
                    ? (CLEAN_ELEC_YEAR_MAX - CLEAN_ELEC_YEAR_MIN)
                    : intervention.max}
                  step={
                    intervention.key === 'vehicleElectrification'
                        ? 0.1
                        : intervention.key === 'gridDecarbonization'
                          ? 1
                        : intervention.key === 'vmtReduction'
                          ? 1
                        : 5
                  }
                  value={intervention.key === 'gridDecarbonization'
                    ? cleanYearToUi(Number(interventionAreas.gridDecarbonization))
                    : Number(interventionAreas[intervention.key]) || 0}
                  onChange={(e) => {
                    if (intervention.key === 'gridDecarbonization') {
                      handleChange(intervention.key, uiToCleanYear(Number(e.target.value)));
                    } else {
                      handleChange(intervention.key, Number(e.target.value));
                    }
                  }}
                  className="compact-slider"
                  style={{
                    background: intervention.key === 'gridDecarbonization'
                      ? `linear-gradient(to right, ${intervention.color} 0%, ${intervention.color} ${(cleanYearToUi(Number(interventionAreas.gridDecarbonization)) / (CLEAN_ELEC_YEAR_MAX - CLEAN_ELEC_YEAR_MIN)) * 100}%, ${sliderTrackEmpty} ${(cleanYearToUi(Number(interventionAreas.gridDecarbonization)) / (CLEAN_ELEC_YEAR_MAX - CLEAN_ELEC_YEAR_MIN)) * 100}%, ${sliderTrackEmpty} 100%)`
                      : (() => {
                          const sliderMin = intervention.min;
                          const sliderMax = intervention.max;
                          const currentValue = Number(interventionAreas[intervention.key]) || 0;
                          
                          const range = sliderMax - sliderMin;
                          if (range <= 0) {
                            return `linear-gradient(to right, ${intervention.color} 0%, ${intervention.color} 0%, ${sliderTrackEmpty} 0%, ${sliderTrackEmpty} 100%)`;
                          }
                          const percentage = ((currentValue - sliderMin) / range) * 100;
                          const clampedPercentage = Math.max(0, Math.min(100, percentage));
                          return `linear-gradient(to right, ${intervention.color} 0%, ${intervention.color} ${clampedPercentage}%, ${sliderTrackEmpty} ${clampedPercentage}%, ${sliderTrackEmpty} 100%)`;
                        })(),
                    '--thumb-border-color': intervention.color === '#bfdbfe' ? '#3b82f6' : intervention.color === '#fef08a' ? '#f59e0b' : '#ea580c',
                  } as React.CSSProperties}
                />
                <div className="compact-slider-value">
                  {intervention.key === 'vehicleElectrification'
                    ? `${(Number(interventionAreas.vehicleElectrification) || 0).toFixed(1)}%`
                    : intervention.key === 'gridDecarbonization'
                      ? `${Math.round(Number(interventionAreas.gridDecarbonization))}`
                      : intervention.key === 'vmtReduction' || intervention.key === 'energyEfficiency'
                        ? `${Number(interventionAreas[intervention.key]).toFixed(1)}%`
                        : `${interventionAreas[intervention.key]}%`}
                </div>
              </div>
            </div>
          </div>

          {/* Energy Efficiency sub-policies - show only when section is expanded */}
          {intervention.key === 'energyEfficiency' && expandedIntervention === 'energyEfficiency' && (
            <>
              {energyEfficiencySubPolicies.map((subPolicy) => (
                <div
                  key={subPolicy.key}
                  className="intervention-sub-policy-row"
                  style={{ backgroundColor: '#fefce8', borderLeftColor: '#f59e0b' }}
                >
                  <div className="intervention-table-label-cell">
                    <span className="intervention-item-label-compact sub-policy-indent">{subPolicy.label}</span>
                  </div>
                  <div className="intervention-table-slider-cell">
                    <div className="compact-slider-wrapper">
                      {subPolicy.key === 'otherEfficiencyPercent' ? (
                        <>
                          <div
                            className="compact-slider-indicator-bar"
                            style={{
                              background: `linear-gradient(to right, #fef08a 0%, #fef08a ${(subPolicy.value / subPolicy.max) * 100}%, #e5e7eb ${(subPolicy.value / subPolicy.max) * 100}%, #e5e7eb 100%)`,
                              pointerEvents: 'none',
                            }}
                          />
                          <div className="compact-slider-value">{subPolicy.value.toFixed(1)}%</div>
                        </>
                      ) : (
                        <>
                          <input
                            type="range"
                            min={subPolicy.min}
                            max={subPolicy.max}
                            step={1}
                            value={subPolicy.value}
                            onPointerDown={(e) => {
                              try {
                                e.currentTarget.setPointerCapture(e.pointerId);
                              } catch {
                                // ignore if capture unsupported
                              }
                            }}
                            onChange={(e) => handleEnergyEfficiencySliderInput(subPolicy.key, Number(e.target.value))}
                            onPointerUp={(e) => {
                              try {
                                e.currentTarget.releasePointerCapture(e.pointerId);
                              } catch {
                                // ignore
                              }
                              finishEnergyEfficiencyDrag();
                            }}
                            onPointerCancel={() => {
                              finishEnergyEfficiencyDrag();
                            }}
                            onBlur={() => {
                              finishEnergyEfficiencyDrag();
                            }}
                            className="compact-slider"
                            style={{
                              background: `linear-gradient(to right, #fef08a 0%, #fef08a ${(subPolicy.value / subPolicy.max) * 100}%, #e5e7eb ${(subPolicy.value / subPolicy.max) * 100}%, #e5e7eb 100%)`,
                              '--thumb-border-color': '#f59e0b',
                            } as React.CSSProperties}
                          />
                          <div className="compact-slider-value">{subPolicy.value.toFixed(1)}%</div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* VMT Reduction Sub-policies */}
          {intervention.key === 'vmtReduction' && expandedIntervention === 'vmtReduction' && (
            <>
              {vmtSubPolicies.map((subPolicy) => (
                <div 
                  key={subPolicy.key}
                  className="intervention-sub-policy-row"
                  style={{ 
                    backgroundColor: '#dbeafe',
                    borderLeftColor: '#3b82f6' 
                  }}
                >
                  <div className="intervention-table-label-cell">
                    {canExpandDetails ? (
                      <button
                        className="intervention-info-button-small"
                        onClick={() => handleSubPolicyClick(subPolicy.key)}
                        title="Click to expand details"
                        aria-expanded={expandedSubPolicy === subPolicy.key}
                      >
                        {expandedSubPolicy === subPolicy.key ? '−' : '+'}
                      </button>
                    ) : null}
                    <span className="intervention-item-label-compact sub-policy-indent">{subPolicy.label}</span>
                  </div>
                  <div className="intervention-table-slider-cell">
                    <div className="compact-slider-wrapper">
                      {subPolicy.key === 'otherInitiatives' ? (
                        // Other Initiatives: Read-only indicator bar (no knob)
                        <>
                          <div
                            className="compact-slider-indicator-bar"
                            style={{
                              background: `linear-gradient(to right, #bfdbfe 0%, #bfdbfe ${((subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)) / subPolicy.max) * 100}%, #e5e7eb ${((subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)) / subPolicy.max) * 100}%, #e5e7eb 100%)`,
                              pointerEvents: 'none',
                            }}
                          />
                          <div className="compact-slider-value">
                            {(subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)).toFixed(1)}%
                          </div>
                        </>
                      ) : (
                        // Other sub-policies: Interactive slider
                        <>
                          <input
                            type="range"
                            min={subPolicy.min}
                            max={subPolicy.max}
                            step={subPolicy.key === 'parkingPricing' ? 10 : subPolicy.key === 'transitOrientedDevelopment' ? 0.1 : subPolicy.key === 'downtownHomesPercent' ? 1 : 1}
                            value={subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)}
                            onChange={(e) => {
                              handleSubPolicyValueChange(subPolicy.key, Number(e.target.value));
                            }}
                            className="compact-slider"
                            style={{
                              background: (() => {
                                const val =
                                  subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0);
                                const rng = subPolicy.max - subPolicy.min;
                                const pct = rng > 0 ? ((val - subPolicy.min) / rng) * 100 : 0;
                                const clamped = Math.max(0, Math.min(100, pct));
                                return `linear-gradient(to right, #bfdbfe 0%, #bfdbfe ${clamped}%, #e5e7eb ${clamped}%, #e5e7eb 100%)`;
                              })(),
                              '--thumb-border-color': '#3b82f6',
                            } as React.CSSProperties}
                          />
                          <div className="compact-slider-value">
                            {(subPolicy.value ?? (subPolicyValues[subPolicy.key as keyof SubPolicyValues] || 0)).toFixed(1)}%
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default InterventionAreasControl;
